import React, { createContext, useContext, ReactNode, useEffect, useState } from "react";
import { Theme } from "src/contexts/ThemeContext";
import { EUrlParams } from "src/types";

interface UrlParamsContextType {
  theme: Theme;
  storedDataKey: string;
  language: string;
  currencyId: string;
  paymentRouteId: string;
  apiUrl: string;
  walletId: string;
  conversion: boolean;
}

const WALLET_ID_PATTERN = /^(MAIN|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

const UrlParamsContext = createContext<UrlParamsContextType | undefined>(undefined);

interface UrlParamsProviderProps {
  children: ReactNode;
}

export const UrlParamsProvider: React.FC<UrlParamsProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(Theme.Light);
  const [storedDataKey, setStoredDataKey] = useState<string>("");
  const [language, setLanguage] = useState<string>("en");
  const [currencyId, setCurrencyId] = useState<string>("");
  const [paymentRouteId, setPaymentRouteId] = useState<string>("");
  const [apiUrl, setApiUrl] = useState<string>("");
  const [walletId, setWalletId] = useState<string>("");
  const [conversion, setConversion] = useState<boolean>(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);

    const themeFromUrl = searchParams.get(EUrlParams.Theme);
    const storedDataKeyFromUrl = searchParams.get(EUrlParams.StoredDataKey);
    const languageFromUrl = searchParams.get(EUrlParams.Language);
    const currencyFromUrl = searchParams.get(EUrlParams.CurrencyId);
    const paymentRouteIdFromUrl = searchParams.get(EUrlParams.PaymentRouteId);
    const apiUrlFromUrl = searchParams.get(EUrlParams.ApiUrl);
    const walletIdFromUrl = searchParams.get(EUrlParams.WalletId);
    const conversionFromUrl = searchParams.get(EUrlParams.Conversion);

    setStoredDataKey(storedDataKeyFromUrl ?? "");
    setLanguage(languageFromUrl ?? "en");
    setCurrencyId(currencyFromUrl ?? "");
    setPaymentRouteId(paymentRouteIdFromUrl ?? "");
    setApiUrl(apiUrlFromUrl ?? "");
    // Mobile 4.33 sends its selected currency as walletId (e.g. walletId=ZAR),
    // which filters balances down to nothing; only MAIN or a UUID is a wallet.
    setWalletId(walletIdFromUrl && WALLET_ID_PATTERN.test(walletIdFromUrl) ? walletIdFromUrl : "");
    setConversion(conversionFromUrl === "true");
    setTheme(themeFromUrl && [Theme.Light, Theme.Dark].includes(themeFromUrl as Theme) ? (themeFromUrl as Theme) : Theme.Light);
  }, []);

  const urlParams = {
    theme,
    storedDataKey,
    language,
    currencyId,
    paymentRouteId,
    apiUrl,
    walletId,
    conversion,
  };

  return <UrlParamsContext.Provider value={urlParams}>{children}</UrlParamsContext.Provider>;
};

export const useUrlParamsContext = (): UrlParamsContextType => {
  const context = useContext(UrlParamsContext);

  if (context === undefined) {
    throw new Error("useUrlParamsContext must be used within a UrlParamsProvider");
  }

  return context;
};
